import { z } from 'zod';
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';
import { pageQuery } from './envelope.ts';
import { kycStatus as accountKycStatus } from './lists.ts';
import { askDeclaredName, calendarDate, declaredNameFields } from './player-auth.ts';

/**
 * Presenting a government ID and a face — PAGCOR item 13, and item 21 fields 9
 * and 10.
 *
 * ── THIS IS NOT THE OLD KYC SUBMISSION, AND THE DIFFERENCE IS THE WHOLE POINT ─
 *
 * The older intake took a KIND and a SHA-256 the browser computed, and no file:
 * it was written when this platform had nowhere to put the bytes of somebody's
 * passport. A reviewer approving one of those approved a 64-character string.
 *
 * There is a vault now (`STORAGE_S3_KYC_BUCKET`, kept apart from the attachment
 * bucket) and an identity provider behind it. So this flow receives the DOCUMENT,
 * has it read by CIRA, and keeps both the images and the extracted fields. It is
 * the only one: the owner closed the older process on 23 October 2026, and its
 * request shapes went with its routes.
 *
 * ── TWO CALLS, NOT ONE ─────────────────────────────────────────────────────
 *
 * The same two-step shape as an attachment, for the same reason: the bytes do
 * not pass through this platform. The first call mints a key and a short-lived
 * signed PUT; the browser sends the photograph straight to the vault; the second
 * tells the platform to go and look. Nothing here describes the file itself —
 * every fact about a stored object is read back from the store, because the only
 * party that can be believed about how many bytes arrived is the one that
 * received them.
 */

/**
 * The three images, and nothing else.
 *
 *   ID_FRONT  item 21 field 10 — "one valid government-issued ID card"
 *   ID_BACK   the same card's reverse, where it carries an address or an MRZ
 *   SELFIE    item 21 field 9 — "latest photo", and item 13's selfie
 *
 * Restated here rather than imported from `@dayon/storage`, which is the same
 * arrangement `attachmentContentType` has and for the same reason: this package
 * is bundled into browsers and must not pull an AWS SDK in behind it. The
 * storage package's test asserts the two agree, which is what stops a
 * duplication becoming a divergence.
 */
export const identityImageKind = z.enum(['ID_FRONT', 'ID_BACK', 'SELFIE']);
export type IdentityImageKind = z.infer<typeof identityImageKind>;

/**
 * What each photograph IS, named once for every screen that shows one.
 *
 * ── WHY THE SELFIE'S NAME MATTERS MORE THAN THE OTHER TWO ─────────────────
 *
 * PAGCOR item 13 does not ask for a selfie. It asks for "a selfie HOLDING THE ID", and
 * that distinction is the whole reason this photograph is collected at all — the
 * identity service runs its face match against its own live capture and never looks at
 * this one, so if it were merely a face it would be redundant.
 *
 * The console called it "Selfie". A reviewer reading that sees a face and ticks it; a
 * reviewer reading "Selfie holding the ID" sees a face with no ID in it and knows the
 * submission does not satisfy the requirement. The label is what makes the rule
 * checkable, and the first real case through this screen was a passport-style headshot
 * with no document in frame that nothing would have caught.
 *
 * ── AND WHY IT LIVES HERE ─────────────────────────────────────────────────
 *
 * It was written out separately in the player's portal and in the operator's console,
 * and the two said different things — "A photo of you" against "Selfie". A player told
 * one thing and a reviewer judging against another is how a requirement quietly stops
 * being enforced. The same correction the work and income codes needed.
 */
export const IDENTITY_IMAGE_LABEL: Readonly<Record<IdentityImageKind, string>> = Object.freeze({
  ID_FRONT: 'Front of the ID',
  ID_BACK: 'Back of the ID',
  SELFIE: 'Selfie holding the ID',
});

/**
 * NO PDF. Narrower than `attachmentContentType`, and it is a decision.
 *
 * An identity image is an input to a face match, not evidence a person reads. A
 * PDF cannot be face-matched and is not among the provider's documented inputs,
 * so accepting one would mean telling the player their documents were received
 * and refusing the check afterwards — when they have already handed over the
 * document and cannot un-hand it.
 */
export const identityImageContentType = z.enum([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
]);
export type IdentityImageContentType = z.infer<typeof identityImageContentType>;

/** 8MB, which is the PROVIDER's limit and not one invented here. */
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

export const identityUploadRequest = z.object({
  kind: identityImageKind,
  contentType: identityImageContentType,
  /**
   * The size the browser believes the photograph is.
   *
   * ADVISORY, exactly as an attachment's is: it lets the platform refuse an
   * oversized image before signing anything, which saves a player on a phone
   * connection a wasted transfer of up to 8MB. What is recorded is read back
   * from the store.
   */
  sizeBytes: z.number().int().positive().max(MAX_IMAGE_BYTES),
});
export type IdentityUploadRequest = z.infer<typeof identityUploadRequest>;

export const identityUploadTicket = z.object({
  kind: identityImageKind,
  /** The vault key. Sent back on the submission so the platform can confirm it. */
  key: z.string(),
  url: z.string(),
  /** Headers the browser must replay on the PUT for the signature to hold. */
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type IdentityUploadTicket = z.infer<typeof identityUploadTicket>;

/**
 * Saying the photographs arrived.
 *
 * THE FRONT AND THE SELFIE ARE BOTH REQUIRED, and that is the correction of the
 * old flow's worst property: its intake asked for "at least one document", so a
 * proof of address alone satisfied it. Without the card there is
 * no document to read; without the face there is nothing connecting the person
 * to the card, which is the whole of item 13's "selfie holding the ID".
 *
 * The back is optional because a passport has no back worth scanning.
 */
export const submitIdentityRequest = z
  .object({
    images: z
      .array(z.object({ kind: identityImageKind, key: z.string().min(1).max(512) }))
      .min(2)
      .max(3),
  })
  .superRefine((value, ctx) => {
    const kinds = value.images.map((i) => i.kind);
    const seen = new Set(kinds);

    if (seen.size !== kinds.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['images'],
        message:
          'Two photographs of the same kind. The reviewer would have no way to say which one ' +
          'the check was run on.',
      });
    }
    if (!seen.has('ID_FRONT')) {
      ctx.addIssue({
        code: 'custom',
        path: ['images'],
        message: 'Send a photo of the front of your ID — there is nothing to read without it.',
      });
    }
    if (!seen.has('SELFIE')) {
      ctx.addIssue({
        code: 'custom',
        path: ['images'],
        message:
          'Send a selfie as well. Without it nothing connects you to the ID, which is the part ' +
          'the check is for.',
      });
    }
  });
export type SubmitIdentityRequest = z.infer<typeof submitIdentityRequest>;

/**
 * What the player is told about their own check.
 *
 * `outcome` is the platform's verdict and is deliberately not the provider's
 * status: a player has no use for "manual_review_required" and the operator's
 * four words cover everything they need to act on.
 */
/**
 * The key `field_confidence` holds the DOCUMENT-level confidence under.
 *
 * CIRA does not return a confidence per field. Its `document_ocr` report carries
 * one number for the whole read — `"confidence": 0.75` on the real response —
 * beside plain string values for the fields themselves. So the per-field map the
 * reviewer's screen was built to render came back `{}` on every case, and every
 * field showed no confidence at all while the screen implied it would.
 *
 * One reserved key rather than copying that number onto each field: a figure the
 * provider gave about a whole document, written against twelve fields, reads as
 * twelve independent measurements and is one. A tier that does return per-field
 * numbers will fill them in under CIRA's own field names, and `document` is not one
 * of those.
 */
export const DOCUMENT_CONFIDENCE_KEY = 'document';

export const identityState = z.object({
  outcome: z.enum(['NONE', 'PENDING', 'VERIFIED', 'REVIEW', 'REFUSED']),
  kycStatus: z.string(),
  canSubmit: z.boolean(),
  /**
   * Whether this deployment has an identity service at all, which decides WHAT
   * KIND OF WAIT the player is in — and the screen may not guess it.
   *
   * It said "the check runs on its own", "usually takes a few minutes" and "we
   * will email you either way" unconditionally. With `CIRA_DRIVER=OFF`, the
   * platform's own default, every one of those was false: nothing ran, nothing
   * updated, nothing was sent, and the account was barred from depositing and
   * playing with no way out. A player who followed the instructions was locked
   * out by a page that told them to wait.
   *
   * False means a person reads every check here. That is a longer wait and a
   * different sentence, and it is not a failure — it is what a deployment with no
   * contracted provider honestly is.
   */
  checksAreAutomatic: z.boolean(),
  /**
   * Whether a person refused one of this player's checks before. Every check after a refusal goes to
   * a person, whatever the identity service says (the settle job's refused-before rule), so the screen
   * may not say a new one usually takes a few minutes — and it cannot tell from the check in front of
   * it, since a new try not yet sent reads like a first one.
   */
  refusedBefore: z.boolean(),
  /**
   * CIRA's hosted live-capture page, while the link is still good.
   *
   * At the biometric tier the provider does not face-match the uploaded selfie. It
   * hosts a page and the player opens it to be captured live — up to three attempts
   * and roughly thirty minutes, then the link is spent. Nothing moves until they do
   * it, so a platform that dropped this url parked every real verification behind a
   * screen reading "Being checked".
   *
   * NULL once it has expired, deliberately: a dead link rendered as a live button is
   * worse than no button, and the screen asks for a new one instead.
   */
  livenessUrl: z.string().nullable(),
  /**
   * Whether the platform is waiting on the PLAYER rather than on the provider.
   *
   * True with an expired link too — the player still has something to do, it is
   * just "ask for another" rather than "open this one".
   */
  awaitingLivenessCapture: z.boolean(),
  /**
   * The sentence beside the control: the TRUE reason it is closed — or, on a refused check the
   * player may try again, what to fix and how many tries are left. Null with nothing to say.
   */
  refusal: z.string().nullable(),
  submittedAt: z.string().nullable(),
  /** Which of the three the vault is holding, so a retry can send only what is missing. */
  imagesHeld: z.array(identityImageKind),
  /** What a person wrote, when a person has looked. Never the provider's raw status. */
  reviewNote: z.string().nullable(),
  /**
   * THE CORRECTION OF THE NAME AND DATE OF BIRTH, while it is offered: the player's current check was
   * refused because the details did not match, they have tries left, no new photos have been sent
   * for this try, and they have not corrected them for this refusal yet. Null otherwise.
   *
   * It carries what the player declares now — the latest correction, else the name given at sign-up
   * and the account's date of birth — for the form to open with. The name is the player's own, sent
   * to them alone; an account opened before sign-up asked for a name has none to send.
   */
  detailsCorrection: z
    .object({
      singleName: z.boolean(),
      firstName: z.string().nullable(),
      middleName: z.string().nullable(),
      lastName: z.string().nullable(),
      /** A key from `DECLARED_NAME_SUFFIXES`; null for none. */
      suffix: z.string().nullable(),
      /** A calendar day, `YYYY-MM-DD`, on the platform's calendar; null when none was recorded. */
      dateOfBirth: z.string().nullable(),
    })
    .nullable(),
});
export type IdentityState = z.infer<typeof identityState>;

/* ══════════════════════════════════════════════════════════════════════════
   THE REVIEW QUEUE — what a person does with a check the provider could not
   finish.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Why a reviewer refused, from a closed set.
 *
 * CLOSED, for the reason `kycRejectionReason` is: a refusal is a decision the
 * player can challenge and a regulator can ask about, and free text cannot be
 * grouped, counted or compared across reviewers. The reviewer's own note travels
 * beside it and is never shown to the player — it is written for the next
 * reviewer and may name a risk signal.
 *
 * Deliberately NOT the same enum as `kycRejectionReason`: that one describes a
 * verdict on the old fingerprint flow, where nobody could open a document. These
 * are the things a person says having LOOKED at a photograph.
 *
 * ── `retry`: WHETHER THE PLAYER CAN PUT IT RIGHT THEMSELVES ──────────────
 *
 * TRUE for a reason about what the player sent or typed, which they can put right
 * on their own: new photographs, an ID from the accepted list, their own typed
 * details. A photograph that could not be read, a document out of date or not
 * accepted, a face that did not match in that light, details that differ from the
 * account. FALSE for a finding about the document or the person, which nothing the
 * player sends changes: a document that appears altered, a person who is not its
 * holder, an ID showing a player under 21.
 *
 * So two reasons are a choice between neighbours. A face that does not match is
 * FACE_DOES_NOT_MATCH when it may be the light, and NOT_THE_HOLDER when it is
 * somebody else. Details that differ are DETAILS_DO_NOT_MATCH, unless the ID shows
 * the player is under 21, which is UNDER_AGE.
 *
 * The flag describes the reason and grants nothing by itself: whether a refused player
 * may send new photos is `identityTriesLeft`'s rule below, which counts their refusals
 * too, and the routes that would take the photographs ask it.
 *
 * ── WHERE A REFUSAL KEEPS ITS REASON ─────────────────────────────────────
 *
 * In its own column, `player.identity_record.refusal_reason`, which a CHECK holds to
 * these keys (`identity_record_refusal_reason_known`, sql/1022). The note beside it
 * holds the reviewer's words alone. A refusal recorded before that column carries
 * its key at the front of the note too, as `KEY: words`. A reason added here is
 * added to that CHECK in the same change: a census test fails until the two agree.
 *
 * UNDER_AGE is last, so the others keep their places. Before it, a reviewer who saw
 * an ID showing an under-age player could only file it as details that do not
 * match — a reason the player can put right.
 */
export const IDENTITY_REFUSAL_REASONS = [
  { key: 'DOCUMENT_UNREADABLE', label: 'The document could not be read', retry: true },
  { key: 'DOCUMENT_EXPIRED', label: 'The document has expired', retry: true },
  { key: 'NOT_A_VALID_ID', label: 'Not an accepted government ID', retry: true },
  { key: 'FACE_DOES_NOT_MATCH', label: 'The face does not match the document', retry: true },
  { key: 'DETAILS_DO_NOT_MATCH', label: 'The details do not match the account', retry: true },
  { key: 'SUSPECTED_TAMPERING', label: 'The document appears altered', retry: false },
  { key: 'NOT_THE_HOLDER', label: 'The person is not the document holder', retry: false },
  { key: 'UNDER_AGE', label: 'The ID shows the player is under 21', retry: false },
] as const;

export const identityRefusalReason = z.enum(
  IDENTITY_REFUSAL_REASONS.map((r) => r.key) as [string, ...string[]],
);
export type IdentityRefusalReason = z.infer<typeof identityRefusalReason>;

/* ══════════════════════════════════════════════════════════════════════════
   TRYING AGAIN AFTER A REFUSAL — the owner, 23 Oct 2026: "Fixable ones", and
   "3, support adds 1".
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * HOW MANY REFUSED CHECKS A PLAYER MAY HAVE, IN ALL.
 *
 * A TRY IS A REFUSED CHECK. Each is a paid check with the identity service, so the number is the
 * owner's, not a feeling about patience. A new live-photo link is not a try: it reuses the same
 * photographs. Here, once, because the player's screen, every door that takes photographs and the
 * console's refusal dialog all count against it.
 */
export const IDENTITY_TRIES = 3;

/** The reasons a player can put right themselves: those marked `retry` above. */
export type FixableRefusalReason = Extract<
  (typeof IDENTITY_REFUSAL_REASONS)[number],
  { retry: true }
>['key'];

/**
 * THE IDs THIS PLATFORM ACCEPTS, in the words a player reads them in — one list for every sentence
 * that names them: the identity page's photo of the front, and what a refusal for "not an accepted
 * ID" asks the player to send instead. The manuals say the same in their own words
 * (docs/manual/player-app.md, "What you send"), and are checked against this by hand.
 */
export const ACCEPTED_IDS = 'a passport, PhilSys ID, UMID, driver’s licence or postal ID';

/**
 * WHAT TO FIX, IN THE PLAYER'S WORDS — one sentence for each reason they can put right.
 *
 * ONLY THOSE. A final reason is never named to the player: a refusal can be a fraud finding, and
 * telling somebody which signal fired teaches them what to change. So the type is keyed by the
 * fixable reasons exactly — a reason marked `retry` with no sentence here, or a sentence for a
 * reason that is not, fails the typecheck.
 *
 * Each sentence stands on its own, naming no screen, button or field, so it can be said wherever a
 * refused player is told what happened.
 *
 * "The details do not match" offers both ways on (the owner, 23 Oct 2026): the player may correct the
 * name and date of birth on their account, once for that refusal, before they send new photos; or the
 * ID may be what was wrong, and new photos of one that shows the details on the account are the
 * answer. It speaks of the account, never of sign-up, because it is said again after a later refusal
 * for the same reason, and by then the details on the account may be a correction rather than what was
 * typed at sign-up. Once they have corrected them for this refusal,
 * `IDENTITY_DETAILS_CORRECTED_SENTENCE` (below) says what is still true.
 */
type FixSentences = Readonly<Record<FixableRefusalReason, string>>;

export const IDENTITY_FIX_SENTENCES: FixSentences = Object.freeze<FixSentences>({
  DOCUMENT_UNREADABLE:
    'We could not read your ID in the photos you sent — take new photos in good light, without ' +
    'flash, with all four corners of the ID in the frame.',
  DOCUMENT_EXPIRED: 'The ID you sent has expired — send photos of an ID that is still valid.',
  NOT_A_VALID_ID:
    `The document you sent is not an ID we accept — send photos of ${ACCEPTED_IDS}.`,
  FACE_DOES_NOT_MATCH:
    'We could not match your face to the photo on your ID — take a new selfie holding the ID, ' +
    'in good light, with your face clear and no hat or sunglasses.',
  DETAILS_DO_NOT_MATCH:
    'The details on your ID do not match the ones on your account — if the name or date of birth on ' +
    'your account is wrong, you can correct them once before you send new photos; otherwise, send ' +
    'photos of an ID that shows the name and date of birth on your account.',
});

/**
 * WHAT A PLAYER REFUSED BECAUSE THE DETAILS DID NOT MATCH IS TOLD ONCE THEY HAVE CORRECTED THEM, in
 * place of that reason's sentence above: the correction for that refusal is used, so it is not
 * offered again, and what is left is the new photos — of an ID showing the details now on the account.
 */
export const IDENTITY_DETAILS_CORRECTED_SENTENCE =
  'The details on your ID did not match the ones on your account, and you have corrected them — ' +
  'now send photos of an ID that shows the name and date of birth on your account.';

/**
 * CORRECTING THE NAME AND THE DATE OF BIRTH TYPED AT SIGN-UP — the owner, 23 Oct 2026: "Yes, keep the
 * first".
 *
 * Offered once for each refusal because the details did not match, before the new photos of that
 * try. The first entry stays as it was given; this is a new row (sql/1023), and the latest one is what
 * the player declares from then on.
 *
 * SIGN-UP'S OWN RULES, NOT A COPY OF THEM: the name group (`declaredNameFields`) and the rules between
 * its boxes (`askDeclaredName`), and the date of birth as a calendar day (`calendarDate`). The route
 * reads the day on the platform's calendar and holds it to 21 or older as sign-up does, and the
 * table's trigger holds it again.
 */
export const declaredDetailsCorrectionRequest = z
  .object({ ...declaredNameFields, dateOfBirth: calendarDate })
  .superRefine((body, ctx) => askDeclaredName(body, ctx));
export type DeclaredDetailsCorrectionRequest = z.infer<typeof declaredDetailsCorrectionRequest>;

/**
 * WHERE A PLAYER'S TRIES STAND: their refused checks so far, current or replaced, and what the
 * latest refusal allows. Read by the identity routes for one player at a time.
 */
export interface IdentityTries {
  /** Every refused check the player has had, the current one and those a later try replaced. */
  refusals: number;
  /** The latest refusal's reason, a key of `IDENTITY_REFUSAL_REASONS`; null with none recorded. */
  latestReason: string | null;
  /** Whether that reason is one the player can put right (`retry`); false with none recorded. */
  latestRetry: boolean;
  /** Whether support allowed one more try after the latest refusal. */
  grantSinceLatestRefusal: boolean;
}

/**
 * HOW MANY MORE CHECKS A REFUSED PLAYER MAY SEND — 0 when whatever happens next happens through
 * support.
 *
 * THE ONE RULE, for every reader: a refusal for a reason the player can put right leaves the tries
 * the player has not used, while they have had fewer than `IDENTITY_TRIES` refusals; a grant made
 * after the latest refusal leaves exactly one, whatever the reason was; anything else leaves none.
 * Asked about a player whose latest check was refused.
 */
export function identityTriesLeft(
  tries: Pick<IdentityTries, 'refusals' | 'latestRetry' | 'grantSinceLatestRefusal'>,
): number {
  if (tries.latestRetry && tries.refusals < IDENTITY_TRIES) return IDENTITY_TRIES - tries.refusals;
  return tries.grantSinceLatestRefusal ? 1 : 0;
}

/**
 * WHETHER THE TRY OPEN NOW IS SUPPORT'S ONE MORE — opened by the grant alone, where the rule above
 * leaves the player none of their own: after a final reason, or with the tries spent. What the
 * player is told says which, because the two are counted differently: a try of their own is one of
 * `IDENTITY_TRIES`, and support's is one more, whatever the count.
 */
export function identityTryOpenedBySupport(
  tries: Pick<IdentityTries, 'refusals' | 'latestRetry' | 'grantSinceLatestRefusal'>,
): boolean {
  return (
    tries.grantSinceLatestRefusal &&
    identityTriesLeft({ ...tries, grantSinceLatestRefusal: false }) === 0
  );
}

/** What a reason under the floor is answered with, under the box: the route's and the dialog's. */
export const ONE_MORE_TRY_REASON_TOO_SHORT = `Say why this player may try once more — at least ${AUDIT_REASON_MIN_LENGTH} characters.`;

/**
 * SUPPORT ALLOWING A REFUSED PLAYER ONE MORE TRY — the owner, 23 Oct 2026: "3, support adds 1", with
 * a logged reason.
 *
 * The reason is the person's own words for the audit trail, floored where every such reason is
 * (`AUDIT_REASON_MIN_LENGTH`): the trail refuses a shorter one, and so does the table the grant is
 * kept in (sql/1024). Never shown to the player. The console asks this same shape whether a reason
 * will be taken, so the two cannot disagree.
 */
export const identityRetryGrantRequest = z.object({
  reason: z
    .string(ONE_MORE_TRY_REASON_TOO_SHORT)
    .trim()
    .min(AUDIT_REASON_MIN_LENGTH, ONE_MORE_TRY_REASON_TOO_SHORT)
    .max(500, 'Keep the reason to 500 characters.'),
});
export type IdentityRetryGrantRequest = z.infer<typeof identityRetryGrantRequest>;

/** One more try, as allowed after a refusal: who (by name), when, and why. */
export interface IdentityAllowedTry {
  /** The member of staff's name; null if their account no longer carries one. */
  by: string | null;
  at: string;
  reason: string;
}

/**
 * WHAT AN IDENTITY CASE SAYS OF ONE MORE TRY, from the decision the press itself is answered by:
 * whether the press would be taken on this check now, and if not, the press's own sentence saying
 * why; and who allowed one more try after this refusal, when and why, if anyone has.
 */
export interface IdentityOneMoreTry {
  canAllow: boolean;
  /** Null exactly when `canAllow`. */
  whyNot: string | null;
  allowed: IdentityAllowedTry | null;
}

/**
 * A reviewer's verdict on one identity check.
 *
 * ── WHY VERIFYING CAN REQUIRE TYPING A NAME ──────────────────────────────
 *
 * `identity_record_verified_has_an_identity` refuses a VERIFIED record without a
 * full name and a date of birth, because a row saying an identity is confirmed
 * with nothing behind it is the defect that table replaced.
 *
 * Most cases arrive with both, read off the document by the provider. The ones
 * that do not are exactly the ones a person is looking at: the provider could not
 * read the card, so there is nothing extracted. That reviewer is holding the
 * answer — item 21 wants the name "as it appears in the ID presented", and a
 * person reading the ID and typing what it says is that, performed by hand.
 *
 * So `fullName` and `dateOfBirth` are optional in the SHAPE and required by the
 * ROUTE when the record lacks them. The route refuses with a sentence naming
 * which, rather than letting the constraint fire as "That change is not allowed
 * by the platform rules".
 */
export const identityVerdictRequest = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('VERIFIED'),
    /** Only needed when the provider extracted nothing. Trimmed, never blank. */
    fullName: z.string().trim().min(2).max(200).optional(),
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.').optional(),
    /** For the next reviewer and the audit trail. Never shown to the player. */
    note: z.string().trim().max(500).optional(),
  }),
  z.object({
    outcome: z.literal('REFUSED'),
    reason: identityRefusalReason,
    /**
     * REQUIRED on a refusal, unlike on an approval.
     *
     * A refusal closes an account's only route to being funded, and the next
     * person to pick the case up — a support agent on the phone to the player —
     * has nothing but this. "DOCUMENT_UNREADABLE" does not say whether the photo
     * was blurred, cropped or a picture of a screen.
     */
    note: z.string().trim().min(10).max(500),
  }),
]);
export type IdentityVerdictRequest = z.infer<typeof identityVerdictRequest>;

/* ══════════════════════════════════════════════════════════════════════════════
   WHEN A LIVE-CAPTURE LINK IS DEAD, AND HOW MANY NEW ONES A PLAYER MAY HAVE
   ══════════════════════════════════════════════════════════════════════════════

   A capture link dies two ways and the platform only ever noticed one.

   IT EXPIRES. Roughly thirty minutes. The screen knew this and said "refresh this
   page in a moment and a new link will be issued" — a remedy nothing implemented.

   OR EVERY ATTEMPT IS SPENT. Three, and the provider reports both numbers. The
   client has parsed them since it was written and nothing stored them, so a player
   who used all three met an enabled "Take the live photo" button that the provider
   would refuse, with no explanation and no way out. The same dead end as expiry,
   reached without waiting for it.

   Either way the account is barred from depositing, playing and withdrawing, and no
   operator screen shows the player at all, because the reviewer's queue selects
   REVIEW and these sit at PENDING. */

/**
 * How many times a player may be given a NEW capture link, over ALL their tries together.
 *
 * THREE. Bounded because every new link means a new verification the operator is charged
 * for, so an unlimited button is a way to spend money by holding down a key. Enough
 * that a bad signal, a dead battery and a lost half hour do not cost somebody their
 * account; few enough that the cost is knowable.
 *
 * ONE SET FOR EVERY TRY — the owner, 25 Oct 2026: "cap it at 6". Each try had three of its
 * own until then. Now a new try after a refusal shares what is left, and every try, support's
 * one more try included, still gets its own first link, which is not one of the three: a try
 * whose first link could not be made would be no try at all. So a player costs at most six
 * paid checks over their three tries (three first links and three new ones), or seven with
 * support's one more try — and one more for each further try support allows, each with its
 * first link only. Once the three are spent, a dead link goes to a person, as before.
 * The API counts them over the player's checks (`currentRecord` in the identity routes).
 *
 * Here rather than in the route because the player's screen says how many are left,
 * and a screen counting down against a different number from the one the server
 * enforces is the drift this codebase keeps finding.
 */
export const MAX_CAPTURE_REISSUES = 3;

/**
 * WHERE THE LIVE-PHOTO STEP HAS GOT TO, decided by the server and never guessed.
 *
 * ── WHY A WORD AND NOT THREE NULLABLE FIELDS ──────────────────────────────
 *
 * The screen worked it out from `livenessUrl === null`, which is true in two states
 * that need opposite sentences: a link that has DIED and one that has not been
 * ISSUED yet. It rendered "that link timed out — ask for a new one" at players whose
 * first link was thirty seconds away.
 *
 * It also could not see a third: a link still inside its half hour with every
 * attempt spent. The server sent the url because the url had not expired, and the
 * screen rendered an enabled button that the provider would refuse, with no
 * explanation and nothing to press instead.
 *
 * A closed set the server decides removes the guessing. The screen renders one
 * branch per word and cannot invent a fourth.
 */
export type CaptureStep =
  /**
   * No live photo in this player's future. Three different reasons, and the screen
   * says nothing about the step in all of them:
   *
   *   no provider on this deployment, so a person reads the documents;
   *   the capture is DONE, so it is behind them;
   *   the provider ANSWERED and asked for no capture at all.
   *
   * The third is the one that needs the `provider_ref` bound below. Without it a
   * verification that came back with no capture url — which `start-identity-checks`
   * forces when a url arrives with no expiry — sat at `WAITING_FOR_LINK` for ever,
   * telling the player a link was "being set up" that nothing would ever set up.
   */
  | 'NONE'
  /**
   * NOT SUBMITTED YET, and this deployment will ask for a live photo.
   *
   * The whole reason the guide exists. The one sentence that used to warn of the
   * extra step was buried in optional help text on the selfie row, and the screen
   * then said "nothing more is needed from you" the moment the documents went in.
   * A player told the step is coming BEFORE they start does not close the tab.
   *
   * Config may speak here because there is no record to read yet — and only here.
   */
  | 'COMING'
  /** Submitted; the provider has not answered yet. A wait, not a failure. */
  | 'WAITING_FOR_LINK'
  /** There is a link and it can be used. */
  | 'LINK_READY'
  /** The clock ran out with attempts to spare. The player may ask for another. */
  | 'LINK_EXPIRED'
  /** Attempts spent, or no links left. A person decides it from here. */
  | 'WITH_A_PERSON';

/** What `captureStepOf` reads. A record, or null when nothing has been sent. */
export interface CaptureRecord extends CaptureState {
  outcome: string;
  submittedAt: Date | string | null;
  /** The provider's own reference, which is NULL until it has answered. */
  providerRef: string | null;
  url: string | null;
  /** The new links the player has been given so far, over all their tries — never this check's alone. */
  reissues: number;
}

/**
 * Where the live-photo step has got to.
 *
 * ── THE RECORD SPEAKS FIRST, AND CONFIG ONLY IN ONE PLACE ─────────────────
 *
 * Config knows what this deployment WILL ask for; the record knows what the
 * provider DID ask for. Wherever both have an opinion the record wins, because a
 * guess that outlives the fact it was guessing about is how a screen ends up parked
 * on a sentence nothing will ever make true.
 *
 * Exactly two states let config speak, and both are states where there is no fact
 * yet: before anything is submitted, and after submission while `providerRef` is
 * still NULL. The moment the provider answers, config is silent.
 *
 * ── THE BUG THIS SHAPE EXISTS BECAUSE OF ──────────────────────────────────
 *
 * The first version returned WAITING_FOR_LINK whenever there was no url, so a
 * verification that came back asking for NO capture — which `start-identity-checks`
 * forces whenever a url arrives with no expiry — told the player a link was "being
 * set up" for ever. `providerRef` is the bound: the same statement writes it and the
 * url together, under `WHERE provider_ref IS NULL`, so a NULL reference means
 * exactly "the provider has not answered".
 */
export function captureStepOf(
  record: CaptureRecord | null,
  livePhotoAsked: boolean,
  now: Date = new Date(),
): CaptureStep {
  /* NOTHING SENT YET. No fact to read, so config may speak — and this is the whole
     point of the guide: a player told the step is coming before they start does not
     close the tab when the screen says the documents are in. */
  if (record === null || record.submittedAt === null) {
    return livePhotoAsked ? 'COMING' : 'NONE';
  }
  /* A decided case has no step left, whatever the capture columns say. */
  if (record.outcome !== 'PENDING') return 'NONE';
  /* DONE is behind them. Not a step they can act on, and not one to warn about. */
  if (record.completedAt !== null) return 'NONE';

  if (record.url !== null) {
    if (captureIsLive(record, now)) return 'LINK_READY';
    if (captureExpiredUnused(record, now) && record.reissues < MAX_CAPTURE_REISSUES) {
      return 'LINK_EXPIRED';
    }
    /* Dead and not recoverable: attempts spent, or no links left. Either way the
       photographs are already held and a person reads them. */
    return 'WITH_A_PERSON';
  }

  /* No url. The provider has either not answered, or answered asking for none. */
  return record.providerRef === null && livePhotoAsked ? 'WAITING_FOR_LINK' : 'NONE';
}

/**
 * The journey as the PLAYER counts it, which is not the four calls the system makes.
 *
 * Both worker jobs are invisible and fold into "we check". The provider's hosted
 * capture — the system's third call — is promoted to a numbered step, announced
 * before the player has taken a single photograph.
 *
 * ── WHY THE WAITING IS A STEP, AND WHY IT NEVER TICKS ─────────────────────
 *
 * A checklist of only the player's actions is complete the instant they finish the
 * live photo. "Complete" to somebody who still cannot deposit reads as "verified",
 * which is the same false reassurance in a new shape. So the last step is titled by
 * WHOSE TURN IT IS, carries no tick, and the verified screen is not a checklist at
 * all — the list is never all-ticked while the account is still shut.
 */
export interface IdentityJourneyStep {
  title: string;
  /** Who has to act. The player must always be able to see whether it is them. */
  whose: 'YOU' | 'US';
  detail: string;
}

/**
 * The steps to show, from the server's own word about the live photo.
 *
 * TWO STEPS OR THREE, decided by the deployment and never guessed in the browser.
 * A site with no provider, or a provider that asks for no capture, shows two — and
 * a screen that showed three there would promise a step nobody would ever ask for.
 */
export function identityJourney(step: CaptureStep): readonly IdentityJourneyStep[] {
  const photographs: IdentityJourneyStep = {
    title: 'Photograph your ID and your face',
    whose: 'YOU',
    detail: 'The front of your ID, the back if it has one, and a selfie holding it.',
  };
  const livePhoto: IdentityJourneyStep = {
    title: 'Take a live photo',
    whose: 'YOU',
    detail:
      'On the checking service\u2019s own page, which opens from here. It asks for your ' +
      'camera. This is the photo that is matched against your ID — the selfie above is ' +
      'the record we keep.',
  };
  const weCheck: IdentityJourneyStep = {
    title: 'We check and answer',
    whose: 'US',
    detail: 'You can close this page once your part is done. We email you either way.',
  };
  /* NONE covers "done" as well as "never asked for", and a finished capture should
     not remove the step the player has just completed from their own list. The
     screen passes the step it was given; a DONE record reaches this as NONE only
     after the list has been rendered against the record's own history. */
  return step === 'NONE' ? [photographs, weCheck] : [photographs, livePhoto, weCheck];
}

/** What the platform knows about one hosted capture, as the rules below read it. */
export interface CaptureState {
  completedAt: Date | string | null;
  urlExpiresAt: Date | string | null;
  attemptsUsed: number | null;
  attemptsAllowed: number | null;
}

/**
 * Whether the player can still use the link they have.
 *
 * NOT the negation of `captureIsDead`: a record with no link at all is neither — the
 * platform has not asked the provider yet, which is a wait, not a failure. Keeping
 * them as two questions is what stops a screen rendering "ask for a new link" at a
 * player whose first one has not been issued.
 */
export function captureIsLive(c: CaptureState, now: Date = new Date()): boolean {
  if (c.completedAt !== null) return false;
  if (c.urlExpiresAt === null) return false;
  if (new Date(c.urlExpiresAt).getTime() <= now.getTime()) return false;
  /* Attempts are only a bar when BOTH numbers are known. One of them missing means
     the provider has not been asked, and treating unknown as exhausted would refuse
     a link that works. */
  if (c.attemptsUsed !== null && c.attemptsAllowed !== null) {
    return c.attemptsUsed < c.attemptsAllowed;
  }
  return true;
}

/**
 * The provider refused this face as many times as it allows.
 *
 * NOT the same as an expired link, and the difference is the whole of the rule
 * below. Unknown counts are NOT spent: NULL means the provider has not been asked.
 */
export function captureAttemptsSpent(c: CaptureState): boolean {
  if (c.attemptsUsed === null || c.attemptsAllowed === null) return false;
  return c.attemptsUsed >= c.attemptsAllowed;
}

/**
 * Dead because the CLOCK ran out while tries remained.
 *
 * ── WHY THIS IS SEPARATE FROM "DEAD" ──────────────────────────────────────
 *
 * The first version of the recovery route offered a new link whenever the capture
 * could no longer be used, which treated these two as one thing:
 *
 *   THE HALF HOUR ELAPSED and the player never opened the page. A bad signal, a
 *   flat battery, a message that arrived at the wrong moment. Nothing about them.
 *
 *   EVERY ATTEMPT WAS SPENT. The provider looked at this person's face, three
 *   times, and refused it three times. That is a FINDING, and the signal a liveness
 *   check exists to produce.
 *
 * Handing the second case a fresh link — and the budget allows three — gives nine
 * attempts across three records that each start clean, which is "keep trying until
 * one passes" with the platform holding the door. A presentation attack is exactly
 * the thing being defended against, and it is the attacker who retries.
 *
 * So only the first is recoverable by the player. The second goes to a person, who
 * has the photographs and can see what the provider could not.
 *
 * Raised by a security review as a liveness bypass, and it was one.
 */
export function captureExpiredUnused(c: CaptureState, now: Date = new Date()): boolean {
  if (c.completedAt !== null) return false;
  if (c.urlExpiresAt === null) return false;
  if (new Date(c.urlExpiresAt).getTime() > now.getTime()) return false;
  return !captureAttemptsSpent(c);
}

/**
 * Whether the capture this player was given can no longer be completed, either way.
 *
 * A link must have EXISTED for it to be dead. Before the worker has created the
 * verification there is nothing to replace, and offering a new link there would
 * charge for a second verification to replace one that was never issued.
 *
 * For what the SCREEN says. What the player may DO about it is
 * `captureExpiredUnused`, which is narrower on purpose.
 */
export function captureIsDead(c: CaptureState, now: Date = new Date()): boolean {
  if (c.completedAt !== null) return false;
  if (c.urlExpiresAt === null) return false;
  return !captureIsLive(c, now);
}

/* ══════════════════════════════════════════════════════════════════════════════
   A DISPLAY NAME, SPLIT INTO THE PARTS AN IDENTITY SERVICE ASKS FOR
   ══════════════════════════════════════════════════════════════════════════════

   ── WHAT THIS IS FOR, AND THE BUG IT EXISTS BECAUSE OF ─────────────────────

   CIRA's subject requires a FIRST and a LAST name as separate fields. This
   platform holds one `display_name`. The job that creates a verification split it
   like this:

       firstName: parts[0]
       lastName:  parts[parts.length - 1]

   For "Segundo Pascua Mercado Jr" that sent first name "Segundo" and last name
   "Jr". The service read SEGUNDO PASCUA MERCADO JR off the licence, compared it
   to "Segundo Jr", and reported — correctly — that the two names do not match.

   The reviewer was then shown "The name on the document is not the name on the
   account" with a face match of 0.9996 against a document that agreed on every
   other field. The mismatch was manufactured here. Observed 7 Oct 2026 on a real
   verification of a real licence.

   ── WHY THE SUFFIX MATTERS PARTICULARLY HERE ──────────────────────────────

   This platform's players are Filipino, and the Philippine convention is
   GIVEN · MIDDLE (the mother's maiden surname) · SURNAME · SUFFIX. A generational
   suffix is ordinary rather than rare, and it is always the LAST token — which is
   exactly the token the old split handed over as the surname. The commonest shape
   of Filipino name was the shape this got most wrong.

   ── AND WHY A HANDLE IS NOT A NAME ────────────────────────────────────────

   `display_name` is a handle. `packages/contracts/src/player-auth.ts` says so in
   as many words, and item 21 asks for the name "as it appears in the ID
   presented" precisely because what this platform collected at registration is
   not that. A handle of one word has no surname in it, and a comparison against
   one is meaningless — so `comparable` says whether a verdict may be shown to a
   reviewer at all, rather than leaving the screen to guess.
*/

/**
 * The generational suffixes this recognises, uppercase and without a full stop.
 *
 * DELIBERATELY SHORT. "V" is left out although it is a real suffix: a lone "V" is
 * far more often a middle initial, and promoting an initial to a suffix would
 * throw away the surname — the same class of mistake this function exists to fix,
 * in the other direction. A suffix this misses stays part of the surname, which
 * is wrong but recoverable; a surname this eats is not.
 */
export const NAME_SUFFIXES: readonly string[] = Object.freeze([
  'JR',
  'SR',
  'II',
  'III',
  'IV',
]);

export interface PersonNameParts {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  /**
   * Whether there is enough here to compare against a document AT ALL.
   *
   * False for a one-word handle, which has no surname in it. A service told to
   * compare its reading against half a name will report a mismatch every time, and
   * a screen that renders that as "the name on the document is not the name on the
   * account" is reporting its own missing data as a finding about a person.
   */
  comparable: boolean;
}

/** `JR.` and `jr` and `Jr` are one suffix. */
function suffixOf(token: string): string | null {
  const bare = token.replace(/\.+$/, '').toUpperCase();
  return NAME_SUFFIXES.includes(bare) ? bare : null;
}

/**
 * Split a display name into the parts an identity service is given.
 *
 * Never throws and never guesses beyond the shape: the FIRST token is the given
 * name, the LAST token that is not a suffix is the surname, and everything
 * between them is the middle name — which is what the Philippine convention
 * puts there. The document remains the authority on the real name; this is the
 * hint the provider compares its reading against.
 */
export function splitPersonName(raw: string | null | undefined): PersonNameParts {
  const none: PersonNameParts = {
    firstName: null,
    middleName: null,
    lastName: null,
    suffix: null,
    comparable: false,
  };
  if (!raw) return none;

  const tokens = raw.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return none;

  /* Suffixes come off the END, and there may be more than one ("Jr III" is
     unusual but costs nothing to allow). Taken from the end so a token that
     merely LOOKS like one mid-name is left where it is. */
  const suffixes: string[] = [];
  while (tokens.length > 1) {
    const found = suffixOf(tokens[tokens.length - 1]!);
    if (found === null) break;
    suffixes.unshift(found);
    tokens.pop();
  }
  const suffix = suffixes.length > 0 ? suffixes.join(' ') : null;

  if (tokens.length === 1) {
    /* One word. A given name, or a handle — and no surname either way, so there
       is nothing a service could compare and no verdict worth rendering. */
    return { firstName: tokens[0]!, middleName: null, lastName: null, suffix, comparable: false };
  }

  const firstName = tokens[0]!;
  const lastName = tokens[tokens.length - 1]!;
  const middle = tokens.slice(1, -1);
  return {
    firstName,
    middleName: middle.length > 0 ? middle.join(' ') : null,
    lastName,
    suffix,
    comparable: true,
  };
}

/* ══════════════════════════════════════════════════════════════════════════════
   THE THREE DAYS, AND THE DUTY TO SAY SO — PAGCOR item 13(a)
   ══════════════════════════════════════════════════════════════════════════════

   The form's own words:

     "a full KYC and identification verification process shall be required to be
      conducted for each of the player prior to his/her first withdrawal or
      cash-out transaction request or within three (3) days from his/her
      registration or sign-up, whichever comes first. IT IS THE RESPONSIBILITY OF
      THE GSA TO PROPERLY NOTIFY THE PROSPECTIVE PLAYER OF THIS 3-DAY KYC"

   TWO DUTIES, and the platform met one. The withdrawal half is enforced harder
   than asked — `assertIdentityVerified` stops money in BOTH directions and
   `playerBarredFromPlay` stops play — so a player cannot cash out unverified.

   The second duty had no implementation at all. Nothing anywhere said "three
   days" to a player: not the registration confirmation, not the identity screen,
   not any email. A rule nobody is told about is not a rule they can keep, and
   the form makes telling them the operator's responsibility rather than an
   optional courtesy.

   ── WHAT THE DEADLINE DOES, AND DELIBERATELY DOES NOT, DO ─────────────────

   It does NOT unlock anything or close anything. An unverified account already
   cannot deposit, play or withdraw from the moment it is opened, which is
   stricter than the rule requires — so passing day three changes nothing about
   what the player may do, and a screen implying otherwise would be inventing a
   penalty the platform does not apply.

   What it does is make the duty VISIBLE: the player is told when their three
   days end, and an operator can see who has passed it. */

/** Three days from registration, per the form. Not a tunable: it is the rule. */
export const KYC_DEADLINE_DAYS = 3;

/** When this player's three days end, and whether they have run out. */
export interface KycDeadline {
  /** ISO instant, three days after registration. */
  endsAt: string;
  /** True once it has passed and the account is still unverified. */
  passed: boolean;
}

/**
 * The deadline for an account, or null when there is nothing to count towards.
 *
 * NULL ONCE VERIFIED, because the duty is discharged and a screen counting down
 * to a date that no longer matters is noise somebody learns to ignore. Null also
 * when registration is unknown, which is a data problem and not a deadline.
 */
export function kycDeadlineOf(
  registeredAt: Date | string | null,
  kycStatus: string,
  now: Date = new Date(),
): KycDeadline | null {
  if (registeredAt === null) return null;
  if (kycStatus === 'VERIFIED') return null;
  const ends = new Date(
    new Date(registeredAt).getTime() + KYC_DEADLINE_DAYS * 24 * 60 * 60 * 1000,
  );
  return { endsAt: ends.toISOString(), passed: ends.getTime() <= now.getTime() };
}

/**
 * What `noCiraCheckOnRecord` reads off a check: who was asked to read it, CIRA's reference, and
 * where it stands.
 */
export interface ProviderReferenceState {
  provider: string;
  providerRef: string | null;
  outcome: string;
}

/**
 * WHETHER CIRA HOLDS NO CHECK THIS PLATFORM CAN POINT TO, for a check sent to CIRA.
 *
 * Such a check reaches a person with no CIRA reference on it: CIRA refused the submission, an
 * image was gone before it could be sent, or the platform could not record the reference CIRA
 * gave (`start-identity-checks`). The platform rules refuse to approve it — an approval of a CIRA
 * check has to point at CIRA's check behind it (sql/992,
 * `identity_record_settled_has_a_provider_ref`) — and the approve route would ask for the name and
 * the date of birth first and only then refuse, with "That change is not allowed by the platform
 * rules.".
 *
 * ONE RULE, HERE, FOR THE TWO PLACES A REVIEWER MEETS IT: the case, which says so before anybody
 * types and closes Verify, and the queue, where the reviewer picks the work. The queue tagged
 * such a check "Nothing read — you type the details" while the case it opened said it could not
 * be approved; computed apart, the two would drift apart again.
 *
 * Not a check still waiting to be sent (PENDING), which has no reference yet, and never a check
 * no service was asked to read (MANUAL), which has none by design and may be approved.
 */
export function noCiraCheckOnRecord(r: ProviderReferenceState): boolean {
  return r.provider === 'CIRA' && r.providerRef === null && r.outcome !== 'PENDING';
}

/** One case in the queue, as a reviewer scans it. */
export const identityQueueRow = z.object({
  id: z.string(),
  playerRef: z.string(),
  playerName: z.string(),
  brandName: z.string().nullable(),
  /** Why it is here, from the job that decided — never the provider's raw status. */
  reviewNote: z.string().nullable(),
  providerStatus: z.string().nullable(),
  faceMatchScore: z.number().nullable(),
  liveness: z.string().nullable(),
  /** Whether the provider extracted anything, so the queue shows the harder cases. */
  hasExtractedIdentity: z.boolean(),
  /**
   * `noCiraCheckOnRecord` for this case — it cannot be approved — computed by the route from the
   * reference, which is never sent: under the mock driver it encodes the person, and the queue
   * carries no identity.
   */
  noCiraCheckOnRecord: z.boolean(),
  imagesHeld: z.array(identityImageKind),
  recordedAt: z.string(),
  waitingDays: z.number().int(),
});
export type IdentityQueueRow = z.infer<typeof identityQueueRow>;

/**
 * The brand chosen in the console's header, which the identity queue and the decided list follow
 * as the payout queue does, by the same rule from the same helper: omitted means every brand the
 * reader may see, and a brand outside their scope is refused.
 */
export const identityQueueQuery = z.object({
  brandId: z.string().optional(),
});

/** The decided list's page, and the same brand. */
export const identityDecidedQuery = pageQuery.extend({
  brandId: z.string().optional(),
});

/* ══════════════════════════════════════════════════════════════════════════
   ONE PLAYER'S IDENTITY CHECKS — GET /api/kyc/identity/players/:playerId
   ══════════════════════════════════════════════════════════════════════════ */

/** Where a check stands: the closed set `player.identity_record.outcome` holds. */
export const identityOutcome = z.enum(['PENDING', 'REVIEW', 'VERIFIED', 'REFUSED']);

/**
 * Where the name or the date of birth on a check came from: the service's reading, a reviewer's
 * typing, or an approval from before the platform recorded which.
 */
export const identitySource = z.enum(['DOCUMENT', 'REVIEWER', 'NOT_RECORDED']);

/** CIRA's sanctions and PEP screening of one check. NULL on a list means CIRA did not screen it. */
const identityScreening = z.object({
  sanctionsHit: z.boolean().nullable(),
  pepHit: z.boolean().nullable(),
  passed: z.boolean().nullable(),
});

/** One check, as the player's KYC tab reads it. */
export const identityRecordFacts = z.object({
  id: z.string(),
  outcome: identityOutcome,
  provider: z.string(),
  /** False once a newer check replaced it. */
  current: z.boolean(),
  submittedAt: z.string().nullable(),
  recordedAt: z.string(),
  supersededAt: z.string().nullable(),
  fullName: z.string().nullable(),
  /** A calendar day, YYYY-MM-DD. */
  dateOfBirth: z.string().nullable(),
  sources: z.object({ fullName: identitySource, dateOfBirth: identitySource }),
  liveness: z.string().nullable(),
  confidence: z.record(z.string(), z.number()),
  /** Who decided, when, and what they wrote — null while nobody has. */
  decision: z
    .object({
      by: z.enum(['SERVICE', 'PERSON']),
      personName: z.string().nullable(),
      at: z.string(),
      reason: z.string().nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  imagesHeld: z.array(identityImageKind),
  imagesPurgedAt: z.string().nullable(),
  screening: identityScreening,
  advice: z.string().nullable(),
  laterAnswer: z
    .object({
      at: z.string(),
      status: z.string(),
      advice: z.string().nullable(),
      screening: identityScreening,
    })
    .nullable(),
  noCiraCheckOnRecord: z.boolean(),
});
/** One check as the KYC tab reads it: the console draws a decided case from the same shape. */
export type IdentityRecordFacts = z.infer<typeof identityRecordFacts>;

/**
 * The whole read: the player's checks, the current one first, and the identity status the
 * ACCOUNT carries — the status deposits, play and withdrawals are checked against.
 *
 * `kycStatus` is read by the SAME statement as the records. The KYC tab compares the two, and the
 * profile's own copy of the status is read once, when the page opens: a decision that lands
 * between that read and this one would make the two disagree on screen while the database holds
 * them in step. A verdict moves the check and the account in one transaction, and one statement
 * sees both or neither.
 */
export const playerIdentityRecords = z.object({
  playerRef: z.string(),
  kycStatus: accountKycStatus,
  records: z.array(identityRecordFacts),
});
export type PlayerIdentityRecordsResponse = z.infer<typeof playerIdentityRecords>;
